/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { fetchGraphQl } from '@/quote-management/api';
import { events } from '@adobe-commerce/event-bus';
import { DUPLICATE_NEGOTIABLE_QUOTE_MUTATION } from './graphql/duplicateNegotiableQuoteMutation';
import { NegotiableQuoteModel } from '@/quote-management/data/models/negotiable-quote-model';
import { transformQuote } from '@/quote-management/data/transforms';
import { state } from '@/quote-management/lib/state';

export interface DuplicateQuoteInput {
  quoteUid: string;
  duplicatedQuoteUid: string;
  hasOutOfStockItems?: boolean;
}

export const duplicateQuote = async (
  input: DuplicateQuoteInput
): Promise<NegotiableQuoteModel | null> => {

  if (!state.authenticated) {
    throw new Error('Unauthorized');
  }

  const { quoteUid, duplicatedQuoteUid, hasOutOfStockItems } = input;

  if (!quoteUid || !quoteUid.trim()) {
    throw new Error('Quote UID is required');
  }

  if (!duplicatedQuoteUid || !duplicatedQuoteUid.trim()) {
    throw new Error('Duplicated Quote UID is required');
  }

  return fetchGraphQl(DUPLICATE_NEGOTIABLE_QUOTE_MUTATION, {
    variables: { quoteUid, duplicatedQuoteUid },
  }).then((response) => {
    const { errors } = response;

    if (errors) {
      const errorMessages = errors.map((error) => error.message).join('; ');
      throw new Error(`Failed to duplicate quote: ${errorMessages}`);
    }

    const quoteData = transformQuote(
      response.data?.duplicateNegotiableQuote?.quote
    );

    if (!quoteData) {
      throw new Error(
        'Failed to transform quote data: Invalid response structure'
      );
    }

    events.emit('quote-management/quote-duplicated', {
      quote: quoteData,
      input: { quoteUid, duplicatedQuoteUid },
      hasOutOfStockItems,
    });

    return quoteData;
  });
};
